import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import Hero from "@/components/Hero";
import IntakeCard from "@/components/IntakeCard";
import EvidenceBoundary from "@/components/EvidenceBoundary";
import ModelRoute from "@/components/ModelRoute";
import AuthorityGate from "@/components/AuthorityGate";
import ReceiptPanel from "@/components/ReceiptPanel";
import AvoidLLMPanel from "@/components/AvoidLLMPanel";
import PatternLibrary from "@/components/PatternLibrary";
import CARTriad from "@/components/CARTriad";
import StepHeader from "@/components/StepHeader";
import {
    approveRoute,
    fetchAvoidExamples,
    fetchCurrentRoute,
    fetchKatlasStatus,
    fetchPatterns,
    refuseRoute,
    reusePattern,
} from "@/lib/api";

const PromptCiergePage = () => {
    const [status, setStatus] = useState(null);
    const [route, setRoute] = useState(null);
    const [patterns, setPatterns] = useState([]);
    const [examples, setExamples] = useState([]);
    const [receipt, setReceipt] = useState(null);
    const [busy, setBusy] = useState(null);

    const loadStatus = useCallback(async () => {
        try {
            const s = await fetchKatlasStatus();
            setStatus(s);
        } catch (e) {
            setStatus({
                mode: "live",
                status: "degraded",
                node_reachable: false,
                label: "KATLAS status unavailable",
            });
        }
    }, []);

    useEffect(() => {
        loadStatus();
        fetchCurrentRoute().then(setRoute).catch(() => {});
        fetchPatterns().then(setPatterns).catch(() => {});
        fetchAvoidExamples().then(setExamples).catch(() => {});
    }, [loadStatus]);

    const handleError = (err) => {
        const detail = err?.response?.data?.detail;
        const message =
            typeof detail === "string"
                ? detail
                : detail?.message || err?.message || "Action failed";
        toast.error(message);
    };

    const doApprove = async () => {
        setBusy("approve");
        try {
            const r = await approveRoute({});
            setReceipt(r);
            toast.success("Bounded prompt route approved · KATLAS receipt signed");
        } catch (e) {
            handleError(e);
        } finally {
            setBusy(null);
        }
    };

    const doRefuse = async () => {
        setBusy("refuse");
        try {
            const r = await refuseRoute({ note: "Reviewer escalation" });
            setReceipt(r);
            toast.success("Refusal / escalation · KATLAS receipt signed");
        } catch (e) {
            handleError(e);
        } finally {
            setBusy(null);
        }
    };

    const doReuse = async (pattern) => {
        setBusy("reuse");
        try {
            const r = await reusePattern({ pattern_id: pattern?.id });
            setReceipt(r);
            toast.success(
                `Reuse approved · ${pattern?.title || "approved pattern"} · KATLAS receipt signed`
            );
        } catch (e) {
            handleError(e);
        } finally {
            setBusy(null);
        }
    };

    const unconfigured = status?.status === "unconfigured" || status?.status === "degraded";

    return (
        <>
            <Hero status={status} />
            <CARTriad />

            <main className="mx-auto max-w-7xl px-6 lg:px-10 py-16 space-y-20">
                {/* Step 1 */}
                <section>
                    <StepHeader
                        step="1"
                        title="Prompt intake"
                        kicker="Read-only summary of the submitted request"
                        testId="step-1"
                    />
                    <IntakeCard route={route} />
                </section>

                {/* Step 2 */}
                <section>
                    <StepHeader
                        step="2"
                        title="Evidence boundary"
                        kicker="What the prompt route may see · what stays at source"
                        testId="step-2"
                    />
                    <EvidenceBoundary
                        shared={route?.evidence_shared}
                        withheld={route?.evidence_withheld}
                    />
                </section>

                {/* Step 3 */}
                <section>
                    <StepHeader
                        step="3"
                        title="Model route and budget rationale"
                        kicker="Reuse approved pattern + low-cost model + human approval"
                        testId="step-3"
                    />
                    <ModelRoute
                        recommended={route?.recommended_route}
                        alternatives={route?.alternatives}
                    />
                </section>

                {/* Step 4 */}
                <section>
                    <StepHeader
                        step="4"
                        title="Authority gate"
                        kicker="Approve · Refuse · Use existing pattern"
                        testId="step-4"
                    />
                    <AuthorityGate
                        onApprove={doApprove}
                        onRefuse={doRefuse}
                        onReuse={() => doReuse()}
                        busy={busy}
                        disabled={unconfigured}
                    />
                </section>

                {/* Step 5 */}
                <section>
                    <StepHeader
                        step="5"
                        title="KATLAS-signed receipt"
                        kicker="Action outcome · signer · node timestamp · CAR block"
                        testId="step-5"
                    />
                    <ReceiptPanel receipt={receipt} />
                </section>

                {/* Avoid LLM call */}
                <AvoidLLMPanel examples={examples} />

                {/* Pattern library */}
                <PatternLibrary patterns={patterns} onReuse={doReuse} />

                <footer className="border-t border-slate-200 pt-10 pb-6 text-xs text-slate-500 font-mono">
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                        <span>PromptCierge · KATLAS / Cierge Foundation</span>
                        <span className="h-1 w-1 rounded-full bg-slate-300" />
                        <span>Receipts prove what happened.</span>
                        <span className="h-1 w-1 rounded-full bg-slate-300" />
                        <span>Evidence stays at source.</span>
                    </div>
                </footer>
            </main>
        </>
    );
};

export default PromptCiergePage;
